/**
 * Score Evaluation Page
 *
 * Loads the evaluation's rubric template (full domain/component/indicator
 * tree) and existing scores, renders the RubricScoringInterface, and submits
 * evaluator-selected scores to the scoring endpoint.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AlertTriangle, Brain, RefreshCw, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '../../components/common/Button';
import { RubricScoringInterface } from '../../components/evaluations/RubricScoringInterface';
import { useDistrictAiScoringAssist } from '../../hooks/useDistrictAiScoringAssist';
import { draftsByComponentId } from '../../services/aiScoringDraftMapper';
import {
  generateComponentDrafts,
  getLatestCompletedRun,
  listComponentDrafts,
} from '../../services/aiScoringService';
import {
  EvaluationDetails,
  RubricTemplateFull,
  evaluationService,
  getEvaluationScores,
  getFullRubricTemplate,
} from '../../services/evaluationService';

/** Danielson 1-4 scale; keep in sync with backend performance-level mapping. */
const LEVEL_SCORES: Record<string, number> = {
  ineffective: 1,
  developing: 2,
  effective: 3,
  highly_effective: 4,
};

const LEVEL_ORDER = [
  'ineffective',
  'developing',
  'effective',
  'highly_effective',
];

interface ComponentScoreDraft {
  score: number;
  level: string;
  notes?: string;
}

export const ScoreEvaluationPage: React.FC = () => {
  const { enabled: aiScoringAssistEnabled } = useDistrictAiScoringAssist();
  const { id: evaluationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [evaluation, setEvaluation] = useState<EvaluationDetails | null>(null);
  const [template, setTemplate] = useState<RubricTemplateFull | null>(null);
  const [scores, setScores] = useState<Record<string, ComponentScoreDraft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiDraftsByComponent, setAiDraftsByComponent] = useState<
    ReturnType<typeof draftsByComponentId>
  >({});
  const [aiDraftStatus, setAiDraftStatus] = useState<
    'idle' | 'loading' | 'ready' | 'empty' | 'error'
  >('idle');
  const [generatingDrafts, setGeneratingDrafts] = useState(false);

  const loadData = useCallback(async () => {
    if (!evaluationId) {
      setError('Evaluation ID is missing');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const evalData = await evaluationService.getEvaluation(evaluationId);
      setEvaluation(evalData);

      const [templateData, existingScores] = await Promise.all([
        getFullRubricTemplate(evalData.rubric_template_id),
        getEvaluationScores(evaluationId),
      ]);
      setTemplate(templateData);

      const initial: Record<string, ComponentScoreDraft> = {};
      for (const cs of existingScores.component_scores) {
        initial[cs.component_id] = {
          score: cs.score,
          level: cs.performance_level ?? 'effective',
          notes: cs.notes ?? undefined,
        };
      }
      setScores(initial);

      if (aiScoringAssistEnabled) {
        setAiDraftStatus('loading');
        try {
          const drafts = await listComponentDrafts(evaluationId);
          setAiDraftsByComponent(draftsByComponentId(drafts));
          setAiDraftStatus(drafts.length > 0 ? 'ready' : 'empty');
        } catch {
          setAiDraftsByComponent({});
          setAiDraftStatus('error');
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load scoring data'
      );
    } finally {
      setLoading(false);
    }
  }, [evaluationId, aiScoringAssistEnabled]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Map the API template tree + local score drafts into the shape the
  // RubricScoringInterface expects.
  const rubricData = useMemo(() => {
    if (!template) return [];
    return [...template.domains]
      .sort((a, b) => a.order_index - b.order_index)
      .map(domain => ({
        id: domain.id,
        name: domain.name,
        description: domain.description ?? '',
        weight: domain.weight,
        order: domain.order_index,
        components: [...domain.components]
          .sort((a, b) => a.order_index - b.order_index)
          .map(component => {
            const draft = scores[component.id];
            const aiDraft = aiDraftsByComponent[component.id];
            return {
              id: component.id,
              code: component.code,
              name: component.name,
              description: component.description ?? '',
              weight: component.weight,
              performance_levels: [...component.indicators]
                .sort(
                  (a, b) =>
                    LEVEL_ORDER.indexOf(a.performance_level) -
                    LEVEL_ORDER.indexOf(b.performance_level)
                )
                .map(indicator => ({
                  level: indicator.performance_level as
                    | 'ineffective'
                    | 'developing'
                    | 'effective'
                    | 'highly_effective',
                  score: LEVEL_SCORES[indicator.performance_level] ?? 0,
                  description: indicator.descriptor,
                  indicators: indicator.keywords ?? [],
                })),
              current_score: draft?.score,
              current_level: draft?.level,
              evaluator_notes: draft?.notes,
              ai_analysis: aiDraft,
            };
          }),
      }));
  }, [template, scores, aiDraftsByComponent]);

  const refreshAiDrafts = async () => {
    if (!evaluationId || !aiScoringAssistEnabled) return;
    setAiDraftStatus('loading');
    try {
      const drafts = await listComponentDrafts(evaluationId);
      setAiDraftsByComponent(draftsByComponentId(drafts));
      setAiDraftStatus(drafts.length > 0 ? 'ready' : 'empty');
    } catch {
      setAiDraftStatus('error');
      toast.error('Could not load AI drafts');
    }
  };

  const handleGenerateAiDrafts = async () => {
    if (!evaluationId) return;
    try {
      setGeneratingDrafts(true);
      const run = await getLatestCompletedRun(evaluationId);
      if (!run) {
        toast.info(
          'No completed transcription run yet. Run AI transcription on this evaluation first.'
        );
        setAiDraftStatus('empty');
        return;
      }
      const drafts = await generateComponentDrafts(evaluationId, run.id);
      setAiDraftsByComponent(draftsByComponentId(drafts));
      setAiDraftStatus(drafts.length > 0 ? 'ready' : 'empty');
      toast.success(
        drafts.length > 0
          ? `Loaded ${drafts.length} AI draft components`
          : 'No observable components received drafts'
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to generate AI drafts'
      );
      setAiDraftStatus('error');
    } finally {
      setGeneratingDrafts(false);
    }
  };

  const scoredCount = Object.keys(scores).length;
  const totalComponents = useMemo(
    () =>
      template
        ? template.domains.reduce((sum, d) => sum + d.components.length, 0)
        : 0,
    [template]
  );

  const handleScoreUpdate = (
    componentId: string,
    score: number,
    level: string,
    notes?: string
  ) => {
    setScores(prev => ({
      ...prev,
      [componentId]: { score, level, notes: notes ?? prev[componentId]?.notes },
    }));
  };

  const handleSubmit = async () => {
    if (!evaluationId || scoredCount === 0) return;
    try {
      setSaving(true);
      const humanOverride: Record<string, { score: number; notes?: string }> =
        {};
      for (const [componentId, draft] of Object.entries(scores)) {
        humanOverride[componentId] = {
          score: draft.score,
          notes: draft.notes,
        };
      }
      const result = await evaluationService.scoreEvaluation(
        evaluationId,
        false,
        humanOverride
      );
      toast.success(
        `Evaluation scored: ${result.total_score.toFixed(2)} (${result.performance_level.replace('_', ' ')})`
      );
      navigate(`/evaluations/${evaluationId}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save scores'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600">Loading rubric...</p>
        </div>
      </div>
    );
  }

  if (error || !evaluation || !template) {
    return (
      <div className="py-8 text-center">
        <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900">
          Error Loading Scoring Data
        </h3>
        <p className="mb-4 text-gray-600">{error || 'Data not found'}</p>
        <Button onClick={() => navigate('/evaluations')}>
          Back to Evaluations
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-start justify-between bg-white px-6 py-4 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Score Evaluation
          </h1>
          <p className="mt-1 text-gray-600">
            {evaluation.teacher?.full_name ||
              `Teacher ${evaluation.teacher_id}`}{' '}
            &middot; {template.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {aiScoringAssistEnabled && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                loading={generatingDrafts}
                onClick={() => void handleGenerateAiDrafts()}
              >
                <Brain className="mr-2 h-4 w-4" />
                Generate AI drafts
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={aiDraftStatus === 'loading'}
                onClick={() => void refreshAiDrafts()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh drafts
              </Button>
            </div>
          )}
          <span className="text-sm text-gray-600">
            {scoredCount}/{totalComponents} components scored
          </span>
          <Button
            onClick={handleSubmit}
            disabled={saving || scoredCount === 0}
            loading={saving}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Scores
          </Button>
        </div>
      </div>

      <RubricScoringInterface
        evaluationId={evaluation.id}
        rubricData={rubricData}
        onScoreUpdate={handleScoreUpdate}
      />
    </div>
  );
};

export default ScoreEvaluationPage;
